import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
    Chip,
    Card,
    CardContent,
    InputAdornment,
    CircularProgress
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    PersonSearch as PersonSearchIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Send as SendIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate, useLocation, useParams } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { EMPLOYEE_STORE, STATUS_SEND } from '../../../config/constants';
import { generateUUID } from '../../../utils/uuid';
import Swal from 'sweetalert2';
import practicalEvaluationService from '../../../services/practicalEvaluationService';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import SelectEmployeesAndTempsModal from '../../jha/SelectEmployeesAndTempsModal';
import useDictation from '../../../hooks/useDictation';

const STATUS_DRAFT = 'draft';
const STATUS_SUBMITTED = 'submitted';

const PracticalEvaluationForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { typeSlug: typeSlugFromParams } = useParams();
    const uuid = searchParams.get('uuid');

    // Check if opened from Training Due
    const selectedTrainingDue = location.state?.selectedTrainingDue;
    const isFromTrainingDue = !!selectedTrainingDue;
    const operatorNameFromTrainingDue = selectedTrainingDue?.fullName || '';
    const operatorIdFromTrainingDue = selectedTrainingDue?.employeeId || '';
    const courseFromTrainingDue = selectedTrainingDue?.course || '';
    const trainingDueId = selectedTrainingDue?.id || null;
    const evaluationTypeSlugFromState = location.state?.evaluationTypeSlug || '';

    // From list creation modal
    const operatorNameFromList = location.state?.operatorName || '';
    const operatorIdFromList = location.state?.operatorId || '';
    const liftTypeFromList = location.state?.liftType || '';

    // Determine typeSlug from URL params or from Training Due state
    const typeSlug = typeSlugFromParams || evaluationTypeSlugFromState || '';

    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [employees, setEmployees] = useState([]);
    const [evaluationType, setEvaluationType] = useState(null);
    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        evaluationTypeSlug: typeSlug,
        evaluationTypeName: '',
        trainingDueId: trainingDueId,
        course: courseFromTrainingDue,
        operatorId: operatorIdFromTrainingDue || operatorIdFromList || '',
        operatorName: operatorNameFromTrainingDue || operatorNameFromList || '',
        evaluatorId: userId,
        evaluatorName: userFullName,
        date: dayjs().format('YYYY-MM-DD'),

        checklistResponses: liftTypeFromList ? { liftType: liftTypeFromList } : {},

        equipmentOperated: '',
        qualifiedEquipment: '',
        notQualifiedEquipment: '',

        comments: '',

        evaluatorSignature: '',
        evaluatorSignatureDate: dayjs().format('YYYY-MM-DD'),
        evaluatorSignatureEmployeeId: userId,
        evaluatorSignatureEmployeeName: userFullName,

        operatorSignature: '',
        operatorSignatureDate: dayjs().format('YYYY-MM-DD'),
        operatorSignatureEmployeeId: '',
        operatorSignatureEmployeeName: '',

        status: STATUS_DRAFT,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    });

    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [operatorModalOpen, setOperatorModalOpen] = useState(false);
    const [evaluatorModalOpen, setEvaluatorModalOpen] = useState(false);
    const [evaluatorSignatureModalOpen, setEvaluatorSignatureModalOpen] = useState(false);
    const [operatorSignatureModalOpen, setOperatorSignatureModalOpen] = useState(false);
    const [setupComplete, setSetupComplete] = useState(() => {
        if (uuid) return true; // editing existing draft
        if (operatorNameFromList) return true; // from list modal (MEWPS or Forklift)
        if (isFromTrainingDue) return true; // from training due — operator and type pre-filled
        return false;
    });

    const saveTimer = useRef(null);
    const evaluatorSigPadRef = useRef(null);
    const operatorSigPadRef = useRef(null);

    // Dictation
    const currentFieldRef = useRef(null);
    const [activeField, setActiveField] = useState(null);
    const {
        isSupported: dictationSupported,
        isListening,
        start: startDictation,
        stop: stopDictation
    } = useDictation({
        lang: 'en-US',
        interimResults: false,
        continuous: false,
        safetyTimeoutMs: 15000,
        prime: true,
    });

    // Load data on mount
    useEffect(() => {
        const loadData = async () => {
            const db = await initializeDB();

            // Load employees
            if (db.objectStoreNames.contains(EMPLOYEE_STORE)) {
                const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
                const store = tx.objectStore(EMPLOYEE_STORE);
                const allEmployees = await store.getAll();
                setEmployees(allEmployees || []);
            }

            if (uuid) {
                // Editing existing draft
                const existingForm = await practicalEvaluationService.getFromIndexedDB(uuid);
                if (existingForm) {
                    setForm(existingForm);
                    setSubmitted(existingForm.status === STATUS_SUBMITTED);

                    // Load the evaluation type from the saved slug
                    if (existingForm.evaluationTypeSlug) {
                        const type = await practicalEvaluationService.getTypeBySlugFromIndexedDB(existingForm.evaluationTypeSlug);
                        setEvaluationType(type);
                    }
                }
            } else if (typeSlug) {
                // New form — load the type definition
                const type = await practicalEvaluationService.getTypeBySlugFromIndexedDB(typeSlug);
                if (type) {
                    setEvaluationType(type);
                    // Auto-detect liftType from course name
                    let autoChecklistResponses = {};
                    if (courseFromTrainingDue) {
                        const courseLower = courseFromTrainingDue.toLowerCase();
                        if (courseLower.includes('boom lift')) {
                            autoChecklistResponses = { liftType: 'Boom Lift' };
                        } else if (courseLower.includes('scissor lift')) {
                            autoChecklistResponses = { liftType: 'Scissor Lift' };
                        }
                    }
                    // Auto-fill equipment operated from type name for forklift types
                    const autoEquipment = type.hasEquipmentFields
                        ? (type.name.match(/\(([^)]+)\)/)?.[1] || type.name)
                        : '';
                    setForm(prev => ({
                        ...prev,
                        evaluationTypeSlug: type.slug,
                        evaluationTypeName: type.name,
                        equipmentOperated: prev.equipmentOperated || autoEquipment,
                        checklistResponses: { ...prev.checklistResponses, ...autoChecklistResponses }
                    }));
                }
            }

            setLoading(false);
        };
        loadData();
    }, [uuid, typeSlug]);

    const handleFieldChange = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    const handleChecklistChange = (key, value) => {
        const updated = {
            ...form,
            checklistResponses: { ...form.checklistResponses, [key]: value }
        };
        setForm(updated);
        queueSave(updated);
    };

    const queueSave = (formData) => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => saveDraft(formData || form), 1000);
    };

    const saveDraft = async (formData) => {
        try {
            const dataToSave = {
                ...(formData || form),
                updatedAt: new Date().toISOString()
            };
            await practicalEvaluationService.saveToIndexedDB(dataToSave);
        } catch (e) {
            console.error('Error saving draft:', e);
        }
    };

    const handleSelectOperator = (selectedArray) => {
        if (!selectedArray || selectedArray.length === 0) {
            setOperatorModalOpen(false);
            return;
        }
        const person = selectedArray[0];
        // Ensure we have a valid ID and Name
        const personId = person.id || person.ID || person.employeeId || person.tempId || '';
        const personName = person.FullName || person.name || '';

        if (!personName) {
            console.error('Selected person has no name:', person);
            setOperatorModalOpen(false);
            return;
        }

        const updated = {
            ...form,
            operatorId: personId,
            operatorName: personName
        };
        setForm(updated);
        queueSave(updated);
        setOperatorModalOpen(false);
    };

    const handleSelectEvaluator = (selectedArray) => {
        if (!selectedArray || selectedArray.length === 0) {
            setEvaluatorModalOpen(false);
            return;
        }
        const person = selectedArray[0];
        // Ensure we have a valid ID and Name
        const personId = person.id || person.ID || person.employeeId || person.tempId || '';
        const personName = person.FullName || person.name || '';

        if (!personName) {
            console.error('Selected person has no name:', person);
            setEvaluatorModalOpen(false);
            return;
        }

        const updated = {
            ...form,
            evaluatorId: personId,
            evaluatorName: personName
        };
        setForm(updated);
        queueSave(updated);
        setEvaluatorModalOpen(false);
    };

    const handleEvaluatorSignatureAccept = () => {
        if (!evaluatorSigPadRef.current) {
            console.error('Evaluator signature pad ref not found');
            return;
        }

        const dataUrl = evaluatorSigPadRef.current.toDataURL();
        const updated = {
            ...form,
            evaluatorSignature: dataUrl,
            evaluatorSignatureDate: dayjs().format('YYYY-MM-DD')
        };
        setForm(updated);
        setEvaluatorSignatureModalOpen(false);
        queueSave(updated);
    };

    const handleOperatorSignatureAccept = () => {
        if (!operatorSigPadRef.current) {
            console.error('Operator signature pad ref not found');
            return;
        }

        const dataUrl = operatorSigPadRef.current.toDataURL();
        const updated = {
            ...form,
            operatorSignature: dataUrl,
            operatorSignatureDate: dayjs().format('YYYY-MM-DD')
        };
        setForm(updated);
        setOperatorSignatureModalOpen(false);
        queueSave(updated);
    };

    const handleClearEvaluatorSignature = () => {
        const updated = {
            ...form,
            evaluatorSignature: '',
            evaluatorSignatureEmployeeId: '',
            evaluatorSignatureEmployeeName: ''
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleClearOperatorSignature = () => {
        const updated = {
            ...form,
            operatorSignature: '',
            operatorSignatureEmployeeId: '',
            operatorSignatureEmployeeName: ''
        };
        setForm(updated);
        queueSave(updated);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!form.operatorName) newErrors.operatorName = 'Operator is required';
        if (!form.evaluatorName) newErrors.evaluatorName = 'Evaluator is required';
        if (!form.date) newErrors.date = 'Date is required';
        if (!form.evaluatorSignature) newErrors.evaluatorSignature = 'Evaluator Signature is required';
        if (!form.operatorSignature) newErrors.operatorSignature = 'Operator Signature is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            Swal.fire('Error', 'Please fill in all required fields', 'error');
            return;
        }

        const result = await Swal.fire({
            title: 'Submit Evaluation?',
            text: 'Once submitted, you cannot edit this evaluation.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, submit it!'
        });

        if (result.isConfirmed) {
            setSaving(true);
            try {
                const submittedForm = {
                    ...form,
                    formType: 'practical-evaluation',
                    status: STATUS_SUBMITTED,
                    submittedAt: new Date().toISOString(),
                    submittedBy: userId,
                    sendBy: userId,
                    submittedByName: userFullName,
                    evaluatorSignatureEmployeeId: form.evaluatorSignatureEmployeeId || userId,
                    evaluatorSignatureEmployeeName: form.evaluatorSignatureEmployeeName || userFullName,
                    equipmentOperated: form.equipmentOperated || '',
                    qualifiedEquipment: form.qualifiedEquipment || '',
                    notQualifiedEquipment: form.notQualifiedEquipment || '',
                };

                let response;
                if (isFromTrainingDue) {
                    // Submit to Training Due endpoint
                    response = await practicalEvaluationService.submitForTrainingDue(submittedForm);
                } else {
                    // Submit to regular endpoint
                    response = await practicalEvaluationService.submit(submittedForm);
                }

                if (response.success || response.status === 'ok') {
                    // Mark as sent so TrainingDue list filters it out
                    await practicalEvaluationService.saveToIndexedDB({
                        ...submittedForm,
                        status: STATUS_SEND,
                    });

                    Swal.fire('Success', 'Evaluation submitted successfully!', 'success');
                    navigate(isFromTrainingDue ? '/training-due' : (typeSlug?.startsWith('forklift-') ? '/training-due/forklift-proficiency' : '/training-due/practical-evaluation'));
                } else {
                    throw new Error(response.message || 'Submission failed');
                }
            } catch (e) {
                console.error('Error submitting:', e);
                Swal.fire('Error', 'Failed to submit evaluation: ' + (e.message || 'Unknown error'), 'error');
            } finally {
                setSaving(false);
            }
        }
    };

    const toggleDictation = async (fieldName) => {
        if (submitted) return;

        const fieldIsActive = isListening && activeField === fieldName;
        if (fieldIsActive) {
            stopDictation();
            setActiveField(null);
            currentFieldRef.current = null;
            return;
        }

        // Stop previous dictation if switching fields
        if (isListening) {
            stopDictation();
        }

        currentFieldRef.current = fieldName;
        setActiveField(fieldName);

        await startDictation({
            onResult: (transcript) => {
                if (!transcript || !currentFieldRef.current) return;

                const field = currentFieldRef.current;
                const currentValue = form[field] || '';
                const newValue = currentValue + (currentValue ? ' ' : '') + transcript;

                const updated = {
                    ...form,
                    [field]: newValue
                };
                setForm(updated);
                queueSave(updated);
            },
            onEnd: () => {
                setActiveField(null);
                currentFieldRef.current = null;
            }
        });
    };

    const ratingType = evaluationType?.ratingType || 'ok_notok';
    const hasItemComments = evaluationType?.hasItemComments || false;
    const hasEquipmentFields = evaluationType?.hasEquipmentFields || false;

    const ratingButtons = ratingType === 'good_fair_poor_na'
        ? [
            { value: 'Good', color: '#4caf50', textColor: 'white' },
            { value: 'Fair', color: '#ff9800', textColor: 'white' },
            { value: 'Poor', color: '#f44336', textColor: 'white' },
            { value: 'N/A', color: '#2196f3', textColor: 'white' },
        ]
        : [
            { value: 'OK', color: '#4caf50', textColor: 'white' },
            { value: 'Not OK', color: '#f44336', textColor: 'white' },
        ];

    const toggleItemCommentDictation = async (itemKey) => {
        if (submitted) return;
        const fieldName = `checklist_comment_${itemKey}`;
        const fieldIsActive = isListening && activeField === fieldName;

        if (fieldIsActive) {
            stopDictation();
            setActiveField(null);
            currentFieldRef.current = null;
            return;
        }

        if (isListening) {
            stopDictation();
        }

        currentFieldRef.current = fieldName;
        setActiveField(fieldName);

        await startDictation({
            onResult: (transcript) => {
                if (!transcript || !currentFieldRef.current) return;
                const commentKey = itemKey + '_comment';
                const currentValue = form.checklistResponses[commentKey] || '';
                const newValue = currentValue + (currentValue ? ' ' : '') + transcript;
                const updated = {
                    ...form,
                    checklistResponses: { ...form.checklistResponses, [commentKey]: newValue }
                };
                setForm(updated);
                queueSave(updated);
            },
            onEnd: () => {
                setActiveField(null);
                currentFieldRef.current = null;
            }
        });
    };

    const renderCheckItem = (item) => {
        const value = form.checklistResponses[item.key];
        const isChoice = item.type === 'choice' && Array.isArray(item.options);
        const commentKey = item.key + '_comment';
        const commentValue = form.checklistResponses[commentKey] || '';

        return (
            <Grid item xs={12} key={item.key}>
                <Box sx={{
                    mb: 1.5,
                    pb: 1.5,
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                                {item.label}
                            </Typography>
                        </Box>
                        <Box>
                            <Stack direction="row" spacing={0.5}>
                                {isChoice ? (
                                    item.options.map(option => (
                                        <Button
                                            key={option}
                                            variant={value === option ? 'contained' : 'outlined'}
                                            color="primary"
                                            size="small"
                                            onClick={() => handleChecklistChange(item.key, option)}
                                            disabled={submitted || isFromTrainingDue}
                                            sx={{ minWidth: 100 }}
                                        >
                                            {option}
                                        </Button>
                                    ))
                                ) : (
                                    ratingButtons.map(btn => (
                                        <Button
                                            key={btn.value}
                                            variant={value === btn.value ? 'contained' : 'outlined'}
                                            size="small"
                                            onClick={() => handleChecklistChange(item.key, btn.value)}
                                            disabled={submitted}
                                            sx={{
                                                minWidth: ratingType === 'good_fair_poor_na' ? 50 : 60,
                                                fontSize: ratingType === 'good_fair_poor_na' ? '0.7rem' : '0.8rem',
                                                px: ratingType === 'good_fair_poor_na' ? 1 : 1.5,
                                                backgroundColor: value === btn.value ? btn.color : 'transparent',
                                                borderColor: btn.color,
                                                color: value === btn.value ? btn.textColor : btn.color,
                                                '&:hover': {
                                                    backgroundColor: value === btn.value ? btn.color : `${btn.color}20`,
                                                    borderColor: btn.color,
                                                },
                                            }}
                                        >
                                            {btn.value}
                                        </Button>
                                    ))
                                )}
                            </Stack>
                        </Box>
                    </Box>
                    {hasItemComments && !isChoice && (
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Comment..."
                            value={commentValue}
                            onChange={(e) => handleChecklistChange(commentKey, e.target.value)}
                            disabled={submitted}
                            sx={{ mt: 0.5 }}
                            InputProps={{
                                sx: { fontSize: '0.8rem' },
                                endAdornment: !submitted && dictationSupported && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleItemCommentDictation(item.key)}
                                            color={isListening && activeField === `checklist_comment_${item.key}` ? 'error' : 'default'}
                                        >
                                            {isListening && activeField === `checklist_comment_${item.key}` ? <MicOffIcon sx={{ fontSize: 16 }} /> : <MicIcon sx={{ fontSize: 16 }} />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    )}
                </Box>
            </Grid>
        );
    };

    if (loading) {
        return (
            <Box sx={{
                height: 'calc(100vh - 60px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    const formTitle = evaluationType ? evaluationType.name : 'Practical Evaluation';

    // Setup screen for new evaluations — pick operator + choice items (e.g. Lift Type) before starting
    if (!setupComplete && evaluationType) {
        const choiceItems = (evaluationType.checklistItems || []).filter(item => item.type === 'choice');

        return (
            <Box sx={{
                height: 'calc(100vh - 60px)',
                backgroundColor: 'white',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate(isFromTrainingDue ? '/training-due' : (typeSlug?.startsWith('forklift-') ? '/training-due/forklift-proficiency' : '/training-due/practical-evaluation'))} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>{formTitle}</Typography>
                    </Box>
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    <Box sx={{ maxWidth: 500, mx: 'auto' }}>
                        <Card elevation={2} sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>Setup Evaluation</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Select operator and evaluation details before starting.
                            </Typography>

                            {/* Operator */}
                            <Box sx={{ mb: 3 }}>
                                <TextField
                                    fullWidth
                                    label="OPERATOR *"
                                    value={form.operatorName}
                                    onClick={() => !isFromTrainingDue && setOperatorModalOpen(true)}
                                    InputProps={{
                                        readOnly: true,
                                        style: {
                                            cursor: isFromTrainingDue ? 'default' : 'pointer',
                                            backgroundColor: isFromTrainingDue ? '#f5f5f5' : 'inherit'
                                        },
                                        endAdornment: !isFromTrainingDue && (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setOperatorModalOpen(true)}>
                                                    <PersonSearchIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    disabled={isFromTrainingDue}
                                    error={!!errors.operatorName}
                                    helperText={isFromTrainingDue ? 'Pre-filled from Training Due' : errors.operatorName}
                                />
                            </Box>

                            {/* Choice items (e.g. Lift Type) */}
                            {choiceItems.map(item => (
                                <Box key={item.key} sx={{ mb: 3 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        {item.label} *
                                    </Typography>
                                    <Stack direction="row" spacing={1}>
                                        {item.options.map(option => (
                                            <Button
                                                key={option}
                                                variant={form.checklistResponses[item.key] === option ? 'contained' : 'outlined'}
                                                color="primary"
                                                size="large"
                                                onClick={() => handleChecklistChange(item.key, option)}
                                                sx={{ flex: 1, py: 1.5 }}
                                            >
                                                {option}
                                            </Button>
                                        ))}
                                    </Stack>
                                </Box>
                            ))}

                            {/* Date */}
                            <Box sx={{ mb: 3 }}>
                                <TextField
                                    fullWidth
                                    label="DATE *"
                                    type="date"
                                    value={form.date}
                                    onChange={(e) => handleFieldChange('date', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>

                            <Button
                                variant="contained"
                                color="success"
                                size="large"
                                fullWidth
                                onClick={() => {
                                    const newErrors = {};
                                    if (!form.operatorName) newErrors.operatorName = 'Please select an operator';
                                    const missingChoice = choiceItems.find(item => !form.checklistResponses[item.key]);
                                    if (missingChoice) newErrors[missingChoice.key] = 'Required';
                                    if (Object.keys(newErrors).length > 0) {
                                        setErrors(newErrors);
                                        return;
                                    }
                                    setErrors({});
                                    setSetupComplete(true);
                                }}
                                sx={{ mt: 1, py: 1.5 }}
                            >
                                Start Evaluation
                            </Button>
                        </Card>
                    </Box>
                </Box>

                {operatorModalOpen && (
                    <SelectEmployeesAndTempsModal
                        open={operatorModalOpen}
                        onClose={() => setOperatorModalOpen(false)}
                        onSelect={handleSelectOperator}
                        title="Select Operator"
                        singleSelect={true}
                        alreadySelected={[]}
                    />
                )}
            </Box>
        );
    }

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate(isFromTrainingDue ? '/training-due' : (typeSlug?.startsWith('forklift-') ? '/training-due/forklift-proficiency' : '/training-due/practical-evaluation'))} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>{formTitle}</Typography>
                    </Box>
                    <Chip
                        label={submitted ? 'Submitted' : (isFromTrainingDue ? 'In Progress' : 'Draft')}
                        size="small"
                        sx={{
                            backgroundColor: submitted ? '#4caf50' : (isFromTrainingDue ? '#ff9800' : '#9e9e9e'),
                            color: 'white'
                        }}
                    />
                </Box>
                {isFromTrainingDue && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 6 }}>
                        Training: {courseFromTrainingDue} | Employee: {operatorNameFromTrainingDue}
                    </Typography>
                )}
            </Box>

            {/* Form Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>

                    {/* Basic Info */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1.5, color: 'primary.main' }}>
                            Evaluation Information
                        </Typography>
                        <Grid container rowSpacing={1} columnSpacing={1}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="OPERATOR *"
                                    value={form.operatorName}
                                    onClick={() => !submitted && !isFromTrainingDue && setOperatorModalOpen(true)}
                                    InputProps={{
                                        readOnly: true,
                                        style: {
                                            cursor: (submitted || isFromTrainingDue) ? 'default' : 'pointer',
                                            backgroundColor: isFromTrainingDue ? '#f5f5f5' : 'inherit'
                                        },
                                        endAdornment: !submitted && !isFromTrainingDue && (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setOperatorModalOpen(true)}>
                                                    <PersonSearchIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    disabled={submitted || isFromTrainingDue}
                                    error={!!errors.operatorName}
                                    helperText={isFromTrainingDue ? 'Pre-filled from Training Due' : errors.operatorName}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="EVALUATOR *"
                                    value={form.evaluatorName}
                                    onClick={() => !submitted && setEvaluatorModalOpen(true)}
                                    InputProps={{
                                        readOnly: true,
                                        style: { cursor: submitted ? 'default' : 'pointer' },
                                        endAdornment: !submitted && (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setEvaluatorModalOpen(true)}>
                                                    <PersonSearchIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    disabled={submitted}
                                    error={!!errors.evaluatorName}
                                    helperText={errors.evaluatorName}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="DATE *"
                                    type="date"
                                    value={form.date}
                                    onChange={(e) => handleFieldChange('date', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    disabled={submitted}
                                    error={!!errors.date}
                                    helperText={errors.date}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Dynamic Checklist */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1.5, color: 'primary.main' }}>
                            Evaluation Topics
                        </Typography>
                        {evaluationType && evaluationType.checklistItems && (() => {
                            const items = evaluationType.checklistItems;
                            const groups = {};
                            items.forEach(item => {
                                const group = item.group || 'General';
                                if (!groups[group]) groups[group] = [];
                                groups[group].push(item);
                            });

                            return Object.entries(groups).map(([groupName, groupItems]) => (
                                <Box key={groupName} sx={{ mb: 2 }}>
                                    {Object.keys(groups).length > 1 && !(groupItems.length === 1 && groupItems[0].label === groupName) && (
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                                            {groupName}
                                        </Typography>
                                    )}
                                    <Grid container>
                                        {groupItems.map(item => renderCheckItem(item))}
                                    </Grid>
                                </Box>
                            ));
                        })()}
                        {(!evaluationType || !evaluationType.checklistItems || evaluationType.checklistItems.length === 0) && (
                            <Typography variant="body2" color="text.secondary">
                                No checklist items defined for this evaluation type.
                            </Typography>
                        )}
                    </Box>

                    {/* Equipment Fields (for forklift types) */}
                    {hasEquipmentFields && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1.5, color: 'primary.main' }}>
                                Equipment Information
                            </Typography>
                            <Grid container rowSpacing={1.5} columnSpacing={1}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="EQUIPMENT OPERATED"
                                        value={form.equipmentOperated}
                                        onChange={(e) => handleFieldChange('equipmentOperated', e.target.value)}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="QUALIFIED EQUIPMENT"
                                        multiline
                                        rows={3}
                                        placeholder="Based on my evaluation, the operator is qualified to operate the following equipment..."
                                        value={form.qualifiedEquipment}
                                        onChange={(e) => handleFieldChange('qualifiedEquipment', e.target.value)}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="NOT QUALIFIED EQUIPMENT"
                                        multiline
                                        rows={3}
                                        placeholder="The operator has not demonstrated competence in operating the following equipment..."
                                        value={form.notQualifiedEquipment}
                                        onChange={(e) => handleFieldChange('notQualifiedEquipment', e.target.value)}
                                        disabled={submitted}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* Comments */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1.5, color: 'primary.main' }}>
                            Comments
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="Enter any additional comments here..."
                            value={form.comments}
                            onChange={(e) => handleFieldChange('comments', e.target.value)}
                            disabled={submitted}
                            InputProps={{
                                endAdornment: !submitted && (
                                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleDictation('comments')}
                                            color={isListening && activeField === 'comments' ? 'error' : 'primary'}
                                        >
                                            {isListening && activeField === 'comments' ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>

                    {/* Signatures */}
                    <Card elevation={1} sx={{ mt: 2 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1.5, color: 'primary.main' }}>
                                Signatures
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                Please review this evaluation with the operator
                            </Typography>

                            <Grid container rowSpacing={2} columnSpacing={{ xs: 1, md: 1.5 }}>
                                {/* Evaluator Signature */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Evaluator: {form.evaluatorName || 'Not selected'}
                                    </Typography>
                                    <Box
                                        sx={{
                                            border: '1px solid #ccc',
                                            borderRadius: 1,
                                            height: 100,
                                            backgroundColor: '#fafafa',
                                            cursor: submitted ? 'default' : 'pointer',
                                            position: 'relative',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden'
                                        }}
                                        onClick={() => !submitted && setEvaluatorSignatureModalOpen(true)}
                                    >
                                        {form.evaluatorSignature ? (
                                            <>
                                                <img
                                                    src={form.evaluatorSignature}
                                                    alt="Evaluator Signature"
                                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                />
                                                {!submitted && (
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleClearEvaluatorSignature();
                                                        }}
                                                        sx={{ position: 'absolute', top: 4, right: 4, minWidth: 'auto', px: 1 }}
                                                    >
                                                        Clear
                                                    </Button>
                                                )}
                                            </>
                                        ) : (
                                            !submitted && (
                                                <Button variant="outlined" size="small">
                                                    Click to Sign
                                                </Button>
                                            )
                                        )}
                                    </Box>
                                    {form.evaluatorSignatureDate && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            Date: {form.evaluatorSignatureDate}
                                        </Typography>
                                    )}
                                    {errors.evaluatorSignature && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                            {errors.evaluatorSignature}
                                        </Typography>
                                    )}
                                </Grid>

                                {/* Operator Signature */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Operator: {form.operatorName || 'Not selected'}
                                    </Typography>
                                    <Box
                                        sx={{
                                            border: '1px solid #ccc',
                                            borderRadius: 1,
                                            height: 100,
                                            backgroundColor: '#fafafa',
                                            cursor: submitted ? 'default' : 'pointer',
                                            position: 'relative',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden'
                                        }}
                                        onClick={() => !submitted && setOperatorSignatureModalOpen(true)}
                                    >
                                        {form.operatorSignature ? (
                                            <>
                                                <img
                                                    src={form.operatorSignature}
                                                    alt="Operator Signature"
                                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                />
                                                {!submitted && (
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleClearOperatorSignature();
                                                        }}
                                                        sx={{ position: 'absolute', top: 4, right: 4, minWidth: 'auto', px: 1 }}
                                                    >
                                                        Clear
                                                    </Button>
                                                )}
                                            </>
                                        ) : (
                                            !submitted && (
                                                <Button variant="outlined" size="small">
                                                    Click to Sign
                                                </Button>
                                            )
                                        )}
                                    </Box>
                                    {form.operatorSignatureDate && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            Date: {form.operatorSignatureDate}
                                        </Typography>
                                    )}
                                    {errors.operatorSignature && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                            {errors.operatorSignature}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Submit Button */}
                    {!submitted && (
                        <Box display="flex" justifyContent="center" sx={{ mt: 2, mb: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleSubmit}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={20} /> : <SendIcon />}
                                sx={{ minWidth: 200 }}
                            >
                                {saving ? 'Submitting...' : 'Submit Evaluation'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Modals */}
            {operatorModalOpen && (
                <SelectEmployeesAndTempsModal
                    open={operatorModalOpen}
                    onClose={() => setOperatorModalOpen(false)}
                    onSelect={handleSelectOperator}
                    title="Select Operator"
                    singleSelect={true}
                    alreadySelected={[]}
                />
            )}

            {evaluatorModalOpen && (
                <SelectEmployeesAndTempsModal
                    open={evaluatorModalOpen}
                    onClose={() => setEvaluatorModalOpen(false)}
                    onSelect={handleSelectEvaluator}
                    title="Select Evaluator"
                    singleSelect={true}
                    alreadySelected={[]}
                />
            )}

            <SignatureModalWithAuto
                open={evaluatorSignatureModalOpen}
                onClose={() => setEvaluatorSignatureModalOpen(false)}
                onAccept={handleEvaluatorSignatureAccept}
                sigPadRef={evaluatorSigPadRef}
                employeeName={form.evaluatorName}
                existingSignature={form.evaluatorSignature}
            />

            <SignatureModalWithAuto
                open={operatorSignatureModalOpen}
                onClose={() => setOperatorSignatureModalOpen(false)}
                onAccept={handleOperatorSignatureAccept}
                sigPadRef={operatorSigPadRef}
                employeeName={form.operatorName}
                existingSignature={form.operatorSignature}
            />
        </Box>
    );
};

export default PracticalEvaluationForm;
